/**
 * Isometric drafting helpers (Faz 7.1 — OCS settings.rs/helpers.rs parity).
 *
 * OCS angle pairs in the active UCS plane (2D = XY here):
 *   Left  = [90°, 150°], Top = [30°, 150°], Right = [30°, 90°]
 * default plane = Left. The pairs are offset by SNAPANG (snap rotation);
 * this build has no SNAPANG sysvar yet, so the offset is 0 (documented).
 *
 * drafting_constrain (OCS helpers.rs): the cursor delta is projected onto
 * the axis with the LARGER |dot product| — never a 45°-bisector heuristic.
 */

import * as THREE from 'three';
import { useViewStore } from '../../state/commandAuxStores';

export type IsoPlane = 'left' | 'top' | 'right';

/** OCS angle table (degrees). */
export const ISO_PLANE_ANGLES: Record<IsoPlane, [number, number]> = {
    left: [90, 150],
    top: [30, 150],
    right: [30, 90],
};

/** Cycle order of the F5 / ISOPLANE loop (OCS IsoPlane::next). */
export const ISO_PLANE_CYCLE: IsoPlane[] = ['left', 'top', 'right'];

/** Live isometric axes (unit vectors) of the active plane; iso drafting
 *  must be on, otherwise null. The 'off' plane value falls back to Left
 *  (the OCS default plane) while the master switch is on. */
export function isoAxes(): [THREE.Vector3, THREE.Vector3] | null {
    const v = useViewStore.getState();
    if (!v.isoDrafting) return null;
    const plane: IsoPlane = v.isoplane === 'off' ? 'left' : v.isoplane;
    return isoAxesFor(plane);
}

export function isoAxesFor(plane: IsoPlane): [THREE.Vector3, THREE.Vector3] {
    const [a1, a2] = ISO_PLANE_ANGLES[plane];
    const r1 = (a1 * Math.PI) / 180, r2 = (a2 * Math.PI) / 180;
    return [
        new THREE.Vector3(Math.cos(r1), Math.sin(r1), 0),
        new THREE.Vector3(Math.cos(r2), Math.sin(r2), 0),
    ];
}

/**
 * OCS drafting_constrain: project the base→point delta onto the dominant
 * iso axis (larger |dot|) and keep only that component.
 */
export function isoConstrain(base: THREE.Vector3, point: THREE.Vector3): THREE.Vector3 {
    const axes = isoAxes();
    if (!axes) return point;
    return isoConstrainWithAxes(base, point, axes);
}

export function isoConstrainWithAxes(
    base: THREE.Vector3,
    point: THREE.Vector3,
    axes: [THREE.Vector3, THREE.Vector3]
): THREE.Vector3 {
    const delta = point.clone().sub(base);
    delta.z = 0;
    const d1 = Math.abs(delta.dot(axes[0]));
    const d2 = Math.abs(delta.dot(axes[1]));
    const axis = d1 >= d2 ? axes[0] : axes[1];
    const along = delta.dot(axis);
    return base.clone().addScaledVector(axis, along).setZ(point.z);
}

/** Cycle the ISOPLANE plane (F5 / ISOPLANE with no argument). */
export function cycleIsoPlane(): IsoPlane {
    const v = useViewStore.getState();
    const current: IsoPlane = v.isoplane === 'off' ? 'left' : v.isoplane;
    const next = ISO_PLANE_CYCLE[(ISO_PLANE_CYCLE.indexOf(current) + 1) % ISO_PLANE_CYCLE.length];
    v.set('isoplane', next);
    // F5 implies the isometric master switch (OCS: ISODRAFT on session).
    if (!v.isoDrafting) v.set('isoDrafting', true);
    return next;
}
